import { ApiError, getUserInfo } from "@storyboard/web-client";
import type { Workspace } from "@storyboard/web-client/auth";
import { transport } from "@/config";
import { isStandaloneQrLoginPage } from "@/init/standalonePage";

export const urlParamsFromWindowPathname = (workspaces: Workspace[]): string | null => {
  if (!workspaces || workspaces?.length === 0) {
    console.error("No valid workspaces provided.");
    return null;
  }
  const urlPath = window?.location?.pathname;
  const workspaceId = urlPath?.includes("/workspaces") ? urlPath?.split("/workspaces/")[1]?.split("/")[0] : null;
  const matchWorkspace = workspaces?.find((w) => w?.id === workspaceId);

  if (!workspaceId && workspaces?.length > 0) {
    return workspaces[0]?.id || null;
  } else if (matchWorkspace?.id) {
    return matchWorkspace?.id;
  } else if (workspaceId && !matchWorkspace) {
    console.error(`Workspace with ID ${workspaceId} not found in the provided workspaces.`);
    return null;
  }

  console.error("No valid workspaces provided.");
  return null;
};

export const user = await (async () => {
  // This runs at import time, before main.tsx's standalone short-circuit can. On /qr-login — a page
  // anonymous visitors load — fetching userinfo would let the transport's 401 handler redirect them
  // to the auth page before the page renders (SVS-3635).
  if (isStandaloneQrLoginPage()) {
    return undefined;
  }
  try {
    return await getUserInfo(transport);
  } catch (e) {
    // The transport already fired the login redirect on a 401; resolving undefined keeps module
    // evaluation alive while the browser navigates instead of failing every importer's boot.
    if (e instanceof ApiError && e.status === 401) {
      return undefined;
    }
    return Promise.reject(e);
  }
})();

// Namespace local data by user in case multiple users share the same browser.
export const userId = user?.accounts[0]?.id ?? null;
export const workspaceId = urlParamsFromWindowPathname(user?.workspaces ?? []);

/**
 * Local data era. 4.x and below were PGlite IndexedDB directories; 5.0 was the in-memory entity
 * store with a whole-collection snapshot database; 6.0 is the same store persisted row-keyed (one
 * object store per collection). `cleanupLegacyDatabases` deletes anything from earlier eras, which
 * is what forces the one-time full bootstrap after this version ships.
 * Use ?dbversion=12345 to route to a fresh scope (support escape hatch, unchanged from PGlite).
 */
const baseVersion = "6.0";
const urlDBVersion =
  typeof window !== "undefined" ? new URLSearchParams(window?.location?.search)?.get("dbversion") : null;
export const version = urlDBVersion ? `v${urlDBVersion}` : `v${baseVersion}`;

/** Scope string for the snapshot DB and the bootstrap registry: data version + user + workspace. */
export const localDataVersion = `${version}-${userId}-${workspaceId}`;

export const snapshotScope = localDataVersion;
